import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import api from "../lib/api";

/** SPT-PTE-2026-0022 — inline typeahead for the Receiving/loading screen's
 *  "Manufacturer" field. Searches EVERY previously-captured manufacturer
 *  across the whole equipment register (not scoped to one customer —
 *  manufacturer brand names are shared, not customer-owned) so staff can
 *  select an existing one instead of re-typing it. Free-text entry always
 *  still works — this is a convenience suggestion list only, never a hard
 *  constraint. Mirrors the existing Part Number / Serial Number typeahead
 *  UX (`EquipmentTypeahead.js`).
 */
export function ManufacturerTypeahead({ value, onChange, placeholder, testid }) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const v = (value || "").trim();
    if (v.length < 2) { setMatches([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/equipment/manufacturer-lookup", { params: { manufacturer: v } });
        setMatches(r.data.matches || []);
      } catch { setMatches([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const v = (value || "").trim();
  const hasResults = open && v.length >= 2;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div className="input" style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px" }}>
        <Search size={13} color="var(--text-3)" />
        <input
          style={{ border: "none", outline: "none", width: "100%", background: "none", fontSize: 13 }}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={testid}
        />
      </div>
      {hasResults && (
        <div className="card" style={{ position: "absolute", zIndex: 20, top: 40, left: 0, right: 0, maxHeight: 220, overflowY: "auto", padding: 4 }}
          data-testid={`${testid}-dropdown`}>
          {loading && <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>Searching…</div>}
          {!loading && matches.length === 0 && (
            <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }} data-testid={`${testid}-no-matches`}>
              No existing manufacturer starts with &quot;{v}&quot; — free text is fine.
            </div>
          )}
          {!loading && matches.map((m) => (
            <button key={m.manufacturer} type="button" className="nav-item"
              style={{ color: "var(--text)", borderRadius: 6, width: "100%", textAlign: "left", padding: "8px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              onClick={() => { onChange(m.manufacturer); setOpen(false); }}
              data-testid={`${testid}-opt-${m.manufacturer}`}>
              <span className="t-strong" style={{ fontSize: 13 }}>{m.manufacturer}</span>
              <span className="mono t-mut" style={{ fontSize: 11 }}>{m.count} record{m.count !== 1 ? "s" : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
